import styled, { keyframes } from "styled-components";

const SYSTEM_FONT =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const popIn = keyframes`
  from { opacity: 0; transform: scale(0.96); }
  to { opacity: 1; transform: scale(1); }
`;

export const Backdrop = styled("div")`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 16px;
  animation: ${fadeIn} 0.18s ease-out;
  font-family: ${SYSTEM_FONT};
`;

export const Dialog = styled("div")`
  position: relative;
  width: 100%;
  max-width: 400px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.2);
  padding: 28px 24px 24px;
  animation: ${popIn} 0.2s ease-out;
  font-family: ${SYSTEM_FONT};
`;

export const ModalBody = styled("div")`
  display: flex;
  flex-direction: column;
  font-family: ${SYSTEM_FONT};
`;

export const CloseButton = styled("button")`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.55);
  z-index: 10;
  transition: background 0.15s ease-in-out, color 0.15s ease-in-out;
  font-family: ${SYSTEM_FONT};

  /* Override the global \`button:hover\` translateY/box-shadow */
  &&:hover,
  &&:focus {
    background: rgba(0, 0, 0, 0.06);
    color: rgba(0, 0, 0, 0.85);
    outline: none;
    transform: none;
    box-shadow: none;
  }

  svg {
    width: 20px;
    height: 20px;
    display: block;
  }
`;

export const Title = styled("div")`
  font-family: ${SYSTEM_FONT};
  font-size: 1.5rem;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 0.5rem;
  text-align: center;
  line-height: 1.3;
`;

export const Subtitle = styled("p")`
  font-family: ${SYSTEM_FONT};
  font-size: 0.95rem;
  color: #6b7280;
  margin: 0 0 1.25rem;
  text-align: center;
  line-height: 1.4;
`;

export const Form = styled("form")`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const PhoneInputRow = styled("div")`
  display: flex;
  align-items: stretch;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  &:focus-within {
    border-color: #000;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.08);
  }
`;

export const CountryCodeSelect = styled("select")`
  appearance: none;
  border: none;
  background: transparent;
  padding: 0 0.25rem 0 0.75rem;
  font-size: 1rem;
  color: #1d1d1f;
  cursor: pointer;
  outline: none;
  font-family: inherit;
  background-image: url("data:image/svg+xml,%3Csvg width='8' height='5' viewBox='0 0 8 5' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l3 3 3-3' stroke='%239ca3af' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.25rem center;
  padding-right: 1.1rem;
`;

export const PrimaryInput = styled("input")`
  width: 100%;
  padding: 0.85rem 1rem;
  font-size: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  color: #1d1d1f;
  outline: none;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  font-family: inherit;

  &:focus {
    border-color: #000;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.08);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

export const PhoneInput = styled(PrimaryInput)`
  border: none;
  border-radius: 0;
  padding-left: 0.5rem;

  &:focus {
    border-color: transparent;
    box-shadow: none;
  }
`;

export const PrimaryButton = styled("button")`
  width: 100%;
  padding: 0.85rem 1rem;
  font-size: 1rem;
  font-weight: 600;
  background: #000;
  color: #fff;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s ease-in-out;
  font-family: ${SYSTEM_FONT};

  /* Higher specificity than the global \`button[type="submit"]:hover\` rule
     in src/styles/styles.ts so we get a clean gray hover instead of the
     site-wide purple gradient + translateY bounce. */
  &&[type="submit"]:hover,
  &&[type="submit"]:focus {
    background: #333333;
    color: #fff;
    outline: none;
    transform: none;
    box-shadow: none;
  }

  &&:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const Divider = styled("div")`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 1.25rem 0;
  color: #9ca3af;
  font-size: 0.85rem;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #e5e7eb;
  }
`;

export const SocialButtons = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

export const LegalNote = styled("p")`
  font-family: ${SYSTEM_FONT};
  font-size: 0.78rem;
  color: #6b7280;
  text-align: center;
  margin: 1.25rem 0 0;
  line-height: 1.5;
`;

export const PrivacyLink = styled("a")`
  color: #6b7280;
  text-decoration: underline;
  text-underline-offset: 2px;

  &&:hover,
  &&:focus {
    color: #1d1d1f;
    transform: none;
    text-decoration: underline;
  }
`;

export const SocialButton = styled("button")`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  width: 100%;
  padding: 0.8rem 1rem;
  font-size: 0.95rem;
  font-weight: 500;
  background: #fff;
  color: #1d1d1f;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s ease-in-out, border-color 0.2s ease-in-out;
  font-family: ${SYSTEM_FONT};

  /* Override the global \`button:hover\` translateY/box-shadow */
  &&:hover,
  &&:focus {
    background: #f9fafb;
    border-color: #d1d5db;
    outline: none;
    transform: none;
    box-shadow: none;
  }

  &&:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #fff;
    border-color: #e5e7eb;
  }

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
`;

export const ErrorMessage = styled("div")`
  font-family: ${SYSTEM_FONT};
  font-size: 0.85rem;
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  margin: 0 0 0.75rem;
  text-align: center;
  line-height: 1.4;
`;

export const CodeInput = styled("input")`
  width: 100%;
  padding: 0.85rem 1rem;
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: 0.6em;
  text-align: center;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  color: #1d1d1f;
  outline: none;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  font-family: ${SYSTEM_FONT};

  &:focus {
    border-color: #000;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.08);
  }

  &::placeholder {
    color: #9ca3af;
    letter-spacing: 0.15em;
    font-size: 1rem;
    font-weight: 400;
  }
`;

export const BackLink = styled("button")`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0;
  margin: 0 0 0.5rem;
  font-size: 0.85rem;
  color: #6b7280;
  cursor: pointer;
  font-family: ${SYSTEM_FONT};

  &&:hover,
  &&:focus {
    color: #1d1d1f;
    outline: none;
    transform: none;
    box-shadow: none;
    text-decoration: underline;
  }
`;

export const ResendRow = styled("div")`
  display: flex;
  justify-content: center;
  margin-top: 0.75rem;
`;

export const ResendButton = styled("button")`
  background: none;
  border: none;
  padding: 0;
  font-size: 0.85rem;
  color: #6b7280;
  cursor: pointer;
  font-family: ${SYSTEM_FONT};
  text-decoration: underline;
  text-underline-offset: 2px;

  &&:hover,
  &&:focus {
    color: #1d1d1f;
    outline: none;
    transform: none;
    box-shadow: none;
  }

  &&:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const EmailHint = styled("p")`
  font-family: ${SYSTEM_FONT};
  font-size: 0.9rem;
  color: #1d1d1f;
  text-align: center;
  margin: 0 0 1rem;
  font-weight: 500;
  word-break: break-all;
`;
